import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { clearExistingAuthSession } from "../../utils/clearAuthSession";
import { establishSession } from "../../utils/establishSession";
import Header from "../Screening/Header";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { applyProfileLanguage } from "../../i18n/profile";

type Status = "loading" | "success" | "error" | "staff_session";

const VerifyEmail = () => {
  const [status, setStatus] = useState<Status>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [signOutLoading, setSignOutLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { isUserAuthenticated, role, authHydrated } = useAppSelector(
    (state) => state.user,
  );
  const { t } = useTranslation("auth");

  const completeVerify = useCallback(
    async (token: string, studySlug: string | null) => {
      const body: { token: string; studySlug?: string } = { token };
      if (studySlug) {
        body.studySlug = studySlug;
      }

      const res = await http.post("/participant/auth/magic-link/verify", body);
      const data = res?.data?.data;

      establishSession(dispatch, {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        id: data.id,
        role: data.role,
        firstName: data.firstName || "",
        lastName: data.lastName || "",
      });
      localStorage.setItem("study-slug", studySlug || "");
      localStorage.setItem("lang", data.lang || "en");
      if (data.studyId) {
        localStorage.setItem("study-id", data.studyId);
      }
      applyProfileLanguage(data.lang || "en");
      navigate(`/participant/screening/${data.studyId}`, {
        replace: true,
      });

      setStatus("success");
    },
    [dispatch, navigate],
  );

  const runVerification = useCallback(
    async (options?: { skipSessionGuard?: boolean }) => {
      const token = searchParams.get("token");
      const studySlug = searchParams.get("studySlug");

      if (!token) {
        setStatus("error");
        setErrorMessage(i18n.t("VERIFY_EMAIL_INVALID_TOKEN", { ns: "auth" }));
        return;
      }

      if (!options?.skipSessionGuard) {
        const hasStoredSession = !!localStorage.getItem("sm-access-token");
        const hasSession = isUserAuthenticated || hasStoredSession;

        if (hasStoredSession && !authHydrated) {
          return;
        }

        if (hasSession && role !== "participant") {
          setStatus("staff_session");
          return;
        }

        if (hasSession && role === "participant") {
          await clearExistingAuthSession(dispatch);
        }
      }

      setStatus("loading");

      try {
        await completeVerify(token, studySlug);
      } catch (err: unknown) {
        setStatus("error");
        const maybeBackendMessage = (
          err as { response?: { data?: { message?: unknown } } }
        )?.response?.data?.message;
        const errorMsg =
          typeof maybeBackendMessage === "string" && maybeBackendMessage.trim()
            ? maybeBackendMessage
            : i18n.t("VERIFY_EMAIL_FAILED", { ns: "auth" });
        setErrorMessage(errorMsg);
        // errorToastMessage(err as Error);
      }
    },
    [
      searchParams,
      role,
      authHydrated,
      isUserAuthenticated,
      dispatch,
      completeVerify,
    ],
  );

  useEffect(() => {
    runVerification();
  }, [searchParams, authHydrated, runVerification]);

  const handleSignOutAndContinue = async () => {
    try {
      setSignOutLoading(true);
      setStatus("loading");
      await clearExistingAuthSession(dispatch);
      await runVerification({ skipSessionGuard: true });
    } catch (err: unknown) {
      setStatus("error");
      setErrorMessage(i18n.t("VERIFY_EMAIL_FAILED", { ns: "auth" }));
      errorToastMessage(err as Error);
    } finally {
      setSignOutLoading(false);
    }
  };

  const handleRetry = () => {
    const studySlug = searchParams.get("studySlug");
    if (studySlug) {
      navigate(`/study/${studySlug}`);
    } else {
      navigate("/");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        bgcolor: "grey.50",
      }}
    >
      <Header />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 448,
            width: "100%",
            borderRadius: 2,
            boxShadow: 4,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {status === "loading" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <CircularProgress
                  size={64}
                  thickness={2}
                  sx={{ mb: 3, color: "primary.main" }}
                />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.primary"
                  gutterBottom
                >
                  {t("VERIFY_EMAIL_AUTHENTICATING_TITLE")}
                </Typography>
                <Typography color="text.secondary">
                  {t("VERIFY_EMAIL_AUTHENTICATING_BODY")}
                </Typography>
              </Box>
            )}

            {status === "success" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    bgcolor: "success.main",
                    color: "success.contrastText",
                    "& .MuiSvgIcon-root": { fontSize: 40 },
                  }}
                >
                  <CheckCircleOutlineIcon />
                </Box>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.primary"
                  gutterBottom
                >
                  {t("VERIFY_EMAIL_SUCCESS_TITLE")}
                </Typography>
                <Typography color="text.secondary">
                  {t("VERIFY_EMAIL_SUCCESS_BODY")}
                </Typography>
              </Box>
            )}

            {status === "staff_session" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    color: "warning.main",
                    "& .MuiSvgIcon-root": { fontSize: 40 },
                  }}
                >
                  <ErrorOutlineIcon />
                </Box>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.primary"
                  gutterBottom
                >
                  {t("VERIFY_EMAIL_STAFF_SESSION_TITLE")}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {t("VERIFY_EMAIL_STAFF_SESSION_BODY")}
                </Typography>
                <Stack spacing={1.5} sx={{ width: "100%" }}>
                  <Button
                    variant="contained"
                    onClick={handleSignOutAndContinue}
                    disabled={signOutLoading}
                    sx={{
                      bgcolor: "primary.dark",
                      "&:hover": { bgcolor: "primary.main" },
                      px: 3,
                    }}
                  >
                    {signOutLoading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      t("VERIFY_EMAIL_SIGN_OUT_CONTINUE_BUTTON")
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRetry}
                    disabled={signOutLoading}
                    sx={{ px: 3 }}
                  >
                    {t("VERIFY_EMAIL_RETRY_BUTTON")}
                  </Button>
                </Stack>
              </Box>
            )}

            {status === "error" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    color: "error.main",
                    "& .MuiSvgIcon-root": { fontSize: 40 },
                  }}
                >
                  <ErrorOutlineIcon />
                </Box>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.primary"
                  gutterBottom
                >
                  {t("VERIFY_EMAIL_ERROR_TITLE")}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {errorMessage}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleRetry}
                  sx={{
                    bgcolor: "primary.dark",
                    "&:hover": { bgcolor: "primary.main" },
                    px: 3,
                  }}
                >
                  {t("VERIFY_EMAIL_RETRY_BUTTON")}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
